// Field, TextArea, DateRange and FieldRow: text entry in the recessed well, the same well as
// Select and SegTrack. An error is a red ring, never a recoloured border.
// docs/operations/design-standard.md, "Controls".
//
// m-0 is load-bearing: the global `input` rule in index.css gives every input `margin: auto`.
// An <input> draws no ::before, so TOUCH cannot grow a field's hit area invisibly; `thumb`
// draws the field taller under a coarse pointer instead.

import React, { useState } from 'react';
import Appear from './Appear';
import Reveal from './Reveal';

export type FieldSize = 'sm' | 'md' | 'lg';
export type FieldVariant = 'well' | 'inline';

export interface FieldLook {
  /** `sm` 28px in rows and dense toolbars, `md` 32px, `lg` 44px on shop-floor and kiosk screens. */
  size?: FieldSize;
  /** `inline` reads as text until hovered, for table cells and property rails. */
  variant?: FieldVariant;
  /** `pill` is for search fields only. */
  shape?: 'control' | 'pill';
  /** Something is wrong with the value: a red ring. Say what in words beside it (FieldRow). */
  error?: boolean;
  /** Needs a look before it is trusted (a reader's guess, a required field still empty): an amber ring. */
  warn?: boolean;
  /** 40px under a coarse pointer, for forms filled on a phone at the bench. A mouse keeps the size. */
  thumb?: boolean;
  /** Multi-line: no fixed height. */
  multiline?: boolean;
}

const HEIGHT: Record<FieldSize, string> = {
  sm: 'h-7 text-[13px]',
  md: 'h-8 text-sm',
  lg: 'h-11 px-3 text-base',
};

const BASE = 'm-0 block w-full min-w-0 max-w-none border-0 text-on-surface placeholder:text-on-surface-variant transition-[box-shadow,background-color,color] duration-quick ease-emphasized focus:outline-none disabled:cursor-default disabled:text-on-surface-disabled';

// Read-only steps down to the raised tone with secondary text: a value nobody may type into must
// not look like a control refusing to work. It stays focusable; Tab still draws its ring.
const WELL = 'bg-surface-container-high px-2.5 shadow-recess focus:bg-surface-container-highest focus:ring-2 disabled:bg-surface-container disabled:shadow-none read-only:bg-surface-container read-only:text-on-surface-variant read-only:shadow-none read-only:focus:bg-surface-container';

// Transparent at rest, the track tone under the pointer, the well itself once it has focus. The
// ring is what survives a row that is itself hovered to the field's tone.
const INLINE = '-mx-1 rounded-inner bg-transparent px-1 hover:bg-surface-container-high focus:bg-surface-container-high focus:shadow-recess focus:ring-2 disabled:bg-transparent read-only:hover:bg-transparent read-only:focus:bg-transparent read-only:focus:shadow-none';

const ring = ({ error, warn }: FieldLook): string => {
  // keep-ring: index.css clears focus rings on a click, and these are state, not focus.
  if (error) return 'keep-ring ring-2 ring-red-500/60 focus:ring-red-500/80';
  if (warn) return 'keep-ring ring-2 ring-amber-500/60 focus:ring-amber-500/80';
  return 'focus:ring-primary/70';
};

/** The field's classes, for an <input> that cannot be a Field (a third-party input, a datalist host). */
export const fieldClass = (look: FieldLook = {}): string => {
  const { size = 'md', variant = 'well', shape = 'control', thumb, multiline } = look;
  if (variant === 'inline') {
    return `${BASE} ${INLINE} ${multiline ? 'py-0.5 leading-5' : 'h-6'} text-sm ${ring(look)}`;
  }
  const corner = shape === 'pill' ? 'rounded-full' : 'rounded-control';
  const height = multiline ? 'py-1.5 text-sm leading-5' : HEIGHT[size];
  const touch = thumb && !multiline ? '[@media(pointer:coarse)]:h-10' : '';
  return `${BASE} ${WELL} ${corner} ${height} ${touch} ${ring(look)}`;
};

const FIGURES = new Set(['date', 'datetime-local', 'time', 'month', 'week', 'number']);

// The built sheet orders `.w-full` after most widths, so a width beside it loses. A width the
// call site names (`w-24`, `!w-16`, `w-[9.5rem]`) replaces it instead; `sm:w-40` needs no help.
const NAMES_WIDTH = /(^|\s)!?w-/;
const sized = (classes: string, className: string): string =>
  NAMES_WIDTH.test(className) ? classes.replace(/(^|\s)w-full(?=\s|$)/, '$1') : classes;

export interface FieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, Omit<FieldLook, 'multiline' | 'shape'> {}

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  ({ size = 'md', variant = 'well', error, warn, thumb, className = '', type = 'text', ...rest }, ref) => {
    // Dates and numbers align as figures; the native calendar glyph takes the pointer.
    const figures = FIGURES.has(type) ? 'tabular-nums [&::-webkit-calendar-picker-indicator]:cursor-pointer' : '';
    return (
      <input
        ref={ref}
        type={type}
        aria-invalid={error || undefined}
        className={`${sized(fieldClass({ size, variant, error, warn, thumb }), className)} ${figures} ${className}`}
        {...rest}
      />
    );
  },
);
Field.displayName = 'Field';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: FieldVariant;
  error?: boolean;
  warn?: boolean;
  /** Lines it grows to before it scrolls. At least `rows`; 8 when not given. */
  maxRows?: number;
}

// Line and padding heights of the two variants (leading-5 with py-1.5 or py-0.5), so the
// limits can be written in lines. Both bound the box, not the text.
const LINE = '1.25rem';
const PAD = { well: '0.75rem', inline: '0.25rem' } as const;

// field-sizing makes the box follow its text. A browser without it ignores the property and the
// two supports- limits with it, so the box stays `rows` lines, as it always was.
const GROWS = '[field-sizing:content] supports-[field-sizing:content]:min-h-[var(--ta-min)] supports-[field-sizing:content]:max-h-[var(--ta-max)]';
// The limits sit inside @supports, after plain utilities in the built sheet, so they would beat a
// height the caller names. A caller that sizes its text area keeps that size and does not grow.
const NAMES_HEIGHT = /(^|\s)!?(min-|max-)?h-/;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ variant = 'well', error, warn, className = '', rows = 3, maxRows, style, ...rest }, ref) => {
    const pad = PAD[variant];
    const limits = {
      '--ta-min': `calc(${rows} * ${LINE} + ${pad})`,
      '--ta-max': `calc(${Math.max(rows, maxRows ?? 8)} * ${LINE} + ${pad})`,
    } as React.CSSProperties;
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={error || undefined}
        className={`${sized(fieldClass({ variant, error, warn, multiline: true }), className)} ${NAMES_HEIGHT.test(className) ? '' : GROWS} ${variant === 'well' ? 'resize-y' : 'resize-none'} ${className}`}
        style={{ ...limits, ...style }}
        {...rest}
      />
    );
  },
);
TextArea.displayName = 'TextArea';

// ── A labelled field ─────────────────────────────────────────────────────────

let seq = 0;

export const labelClass = 'block text-[13px] font-medium leading-[18px] text-on-surface';

export interface FieldRowProps {
  label: React.ReactNode;
  /** The control's id. Taken from the child, or made up, when absent. */
  htmlFor?: string;
  /** A quieter line under the control: what the value is for, its format. */
  hint?: React.ReactNode;
  /** What is wrong, in words, under the control. Pair it with the control's own `error`. */
  error?: React.ReactNode;
  /** What to check, in words. Pair it with the control's own `warn`. */
  warn?: React.ReactNode;
  className?: string;
  /** One control. It receives the id and is described by the hint and the message. */
  children: React.ReactElement<{ id?: string; 'aria-describedby'?: string }>;
}

/** A label above one control, with an optional hint and a message that grows into place. */
export const FieldRow: React.FC<FieldRowProps> = ({ label, htmlFor, hint, error, warn, className = '', children }) => {
  const [made] = useState(() => `ui-field-${++seq}`);
  const id = htmlFor || children.props.id || made;
  const message = error || warn;
  const described = [children.props['aria-describedby'], hint ? `${id}-hint` : '', message ? `${id}-msg` : '']
    .filter(Boolean).join(' ') || undefined;
  const control = React.cloneElement(children, { id, 'aria-describedby': described });
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <div className="mt-1.5">{control}</div>
      <Reveal show={!!message}>
        <p id={`${id}-msg`} className={`m-0 pt-1.5 text-xs ${error ? 'text-red-300' : 'text-amber-300'}`}>{message}</p>
      </Reveal>
      {hint && <p id={`${id}-hint`} className="m-0 mt-1.5 text-xs text-on-surface-variant">{hint}</p>}
    </div>
  );
};

// ── A date range ─────────────────────────────────────────────────────────────

export interface DateRangeProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  /** Names the pair for a reader ("Pulled between"). The ends are "From" and "To" inside it. */
  label: string;
  type?: 'date' | 'datetime-local';
  size?: FieldSize;
  disabled?: boolean;
  className?: string;
}

/**
 * Two dates that bound each other: the start cannot pass the end in the picker, and a typed
 * range that runs backwards rings both ends red and says so beside them.
 */
export const DateRange: React.FC<DateRangeProps> = ({ from, to, onChange, label, type = 'date', size = 'md', disabled, className = '' }) => {
  const [made] = useState(() => `ui-range-${++seq}`);
  const backwards = !!from && !!to && from > to;
  const width = type === 'date' ? 'w-[9.5rem]' : 'w-[13.5rem]';
  return (
    <div role="group" aria-label={label} className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      <Field
        type={type}
        size={size}
        aria-label="From"
        value={from}
        max={to || undefined}
        error={backwards}
        disabled={disabled}
        aria-describedby={backwards ? `${made}-msg` : undefined}
        onChange={(e) => onChange({ from: e.target.value, to })}
        className={width}
      />
      <span className="text-xs text-on-surface-variant">to</span>
      <Field
        type={type}
        size={size}
        aria-label="To"
        value={to}
        min={from || undefined}
        error={backwards}
        disabled={disabled}
        aria-describedby={backwards ? `${made}-msg` : undefined}
        onChange={(e) => onChange({ from, to: e.target.value })}
        className={width}
      />
      <Appear show={backwards} className="inline-flex">
        <span id={`${made}-msg`} className="text-xs text-red-300">Ends before it starts</span>
      </Appear>
    </div>
  );
};

export default Field;
